import { reactive, computed } from 'vue'

import cloneDeep from 'lodash/cloneDeep'
import buffer from '@turf/buffer'
import bbox from '@turf/bbox'
import { point } from '@turf/turf'

// Libs
import split from '@/lib/split'
import {
  COUNTRY_CODES,
  MAP_MIN_ZOOM,
  STATE_CODES,
  US_STATE_CODES,
} from '@/lib/enums'
import { httpGetJson } from '@/lib/net'
import { remapRange } from '@/lib/math'
import {
  SEARCH_ORIGINS,
  CARD_ORIGINS,
  SEARCH_RESULT_SELECTED_ORIGINS,
  EVENTS,
} from '@/lib/enums-marketing'
import { examplesByVertical } from '@/types/search-examples'
import {
  getRouterDestinationForKgType,
  isSupportedKgType,
} from '@/lib/knowledge-graph/utils'
import { SUPPORTED_STATES } from '@/fish/lib/enums'
import { ONXFONT_API_PATH } from '@/lib/glyphs'
import {
  gqlFilterExpressionToJson,
  gqlFilterSlugEquals,
  gqlFilterSlugNotEquals,
} from '@/lib/graph-filter-utils'
import { isExploreEnabled } from '@/lib/prototyping-flags'

// Plugins
import { getItem, saveItem } from '@/plugins/storage'

// Types
import type {
  SearchHistoryType,
  SearchRequestReturnType,
  SearchResultCacheKey,
  SearchResultProps,
  CoordinateResult,
  SearchResultAboutTypename,
} from '@/services/search-service/types'
import {
  isAutocompleteKgResult,
  type AutocompleteResult,
  type SearchResultItem,
} from '@onxmaps/search-web'
import type Geographic from '@/lib/geographic'
import type { SearchFilter, SearchResultAbout } from '@onxmaps/supergraph'
import type { KgType } from '@/lib/knowledge-graph/types'
import type { BBox, Feature, Point, Position } from 'geojson'

// Services

import subscriptionService from '@/services/subscription-service'
import marketingEventService from '@/services/marketing-event-service'
import {
  camera,
  mapReadyPromise,
  replaceMarkers,
  searchPinFeatures,
  setSearchPinSelected,
  visibleRegion,
} from '@/services/map-service'
import {
  HISTORY_ITEM_LIMIT,
  SearchMode,
  SEARCH_BACK_ROUTE,
} from '@/services/search-service/constants'
import { getSearchResultDefault } from '@/services/search-service/search-result'
import { hasMinMaxFilter } from '@/services/search-filter-service'
import { SEARCH_FILTER_KEYS } from '@/services/search-filter-service-constants'

// Supergraph Operations
import {
  searchLocation,
  searchPrivateLand,
} from '@/services/search-service/operations'

// Router
import router from '@/router'
import { trackUserAction } from '@/services/user-action-service'
import { isActive } from '@/services/feature-flag-service'
import { fetchAutocompleteResultItemData } from '@/lib/search/search-autocomplete'
import {
  displaySearchResultPins,
  generatePinFeatureFromSearchResult,
} from '@/lib/search/search-result-pins'
import { generateGlyphMarkers } from '@/lib/glyph-markers'
import { GLYPH_FONTS } from '@/lib/glyph-markers-constants'

type SearchServiceState = {
  searchIsActive: boolean
  searchMode: SearchMode
  searchOrigin: string
  isLoading: boolean
  showSearchArea: boolean
  queryInput: string
  searchCompleted: boolean
  lastQueryInputFetchedLocationSearch: string
  lastQueryInputFetchedLandownerSearch: string
  locationResults: SearchResultItem[]
  landownerResults: SearchResultItem[]
  totalLocationResults: number
  totalLandownerResults: number
  glyphIconMap: Record<string, unknown>
  zeroQueryScope: string | null
  isExploreMapView: boolean
  clickedBackFromResultCard: boolean
}

// Handle text input and display control for search input
const observableState = reactive<SearchServiceState>({
  searchIsActive: false,
  searchMode: SearchMode.Location,
  searchOrigin: SEARCH_ORIGINS.SEARCH_BAR,
  isLoading: false,
  showSearchArea: false,
  queryInput: '',
  searchCompleted: false,
  lastQueryInputFetchedLocationSearch: '',
  lastQueryInputFetchedLandownerSearch: '',
  locationResults: [],
  landownerResults: [],
  totalLocationResults: 0,
  totalLandownerResults: 0,
  glyphIconMap: {},
  zeroQueryScope: null,
  isExploreMapView: false,
  clickedBackFromResultCard: false,
})

//Set and retrieve isLoading
export const isLoading = computed(() => observableState.isLoading)
/**
 * *(AI-generated)*
 * Sets the loading state.
 */
export const setIsLoading = (loading: boolean) =>
  (observableState.isLoading = loading)

//Set and retrieve search mode
/**
 * *(AI-generated)*
 * Returns the current search mode.
 */
export const searchMode = () => observableState.searchMode
/**
 * *(AI-generated)*
 * Sets the current search mode.
 */
export const setSearchMode = (mode: SearchMode) =>
  (observableState.searchMode = mode)

//Set and retrieve search origin
/**
 * *(AI-generated)*
 * Returns the current search origin.
 */
export const searchOrigin = () => observableState.searchOrigin
/**
 * *(AI-generated)*
 * Sets the current search origin.
 */
export const setSearchOrigin = (origin: string) =>
  (observableState.searchOrigin = origin)

//Set and retrieve query input
export const queryInput = computed(() => observableState.queryInput)
/**
 * *(AI-generated)*
 * Sets the query input.
 */
export const setQueryInput = (input: string) =>
  (observableState.queryInput = input)

// Set and retrieve search completed status - indicating whether the search has finished
/**
 * *(AI-generated)*
 * Returns whether the search has completed.
 */
export const searchCompleted = () => observableState.searchCompleted
/**
 * *(AI-generated)*
 * Sets the search completed state.
 */
export const setSearchCompleted = (completed: boolean) =>
  (observableState.searchCompleted = completed)

// Set and retrieve last location search
/**
 * *(AI-generated)*
 * Returns the last location search query.
 */
export const lastQueryInputFetchedLocationSearch = () =>
  observableState.lastQueryInputFetchedLocationSearch
/**
 * *(AI-generated)*
 * Sets the last location search query.
 */
export const setLastQueryInputFetchedLocationSearch = (input: string) =>
  (observableState.lastQueryInputFetchedLocationSearch = input)

// Set and retrieve last landowner search
/**
 * *(AI-generated)*
 * Returns the last landowner search query.
 */
export const lastQueryInputFetchedLandownerSearch = () =>
  observableState.lastQueryInputFetchedLandownerSearch
/**
 * *(AI-generated)*
 * Sets the last landowner search query.
 */
export const setLastQueryInputFetchedLandownerSearch = (input: string) =>
  (observableState.lastQueryInputFetchedLandownerSearch = input)

// Set and retrieve status of whether search is open or not
/**
 * *(AI-generated)*
 * Sets whether the search is active.
 */
export const setSearchIsActive = (value: boolean) =>
  (observableState.searchIsActive = value)
/**
 * *(AI-generated)*
 * Returns whether the search is active.
 */
export const searchIsActive = () => observableState.searchIsActive

// Set and retrieve if "search this area" should show
/**
 * *(AI-generated)*
 * Sets whether the search area indicator should be shown.
 */
export const setShowSearchArea = (value: boolean) =>
  (observableState.showSearchArea = value)
/**
 * *(AI-generated)*
 * Returns whether the search area indicator should be shown.
 */
export const showSearchArea = () => observableState.showSearchArea

// Set and retrieve location search results
/**
 * *(AI-generated)*
 * Sets the location search results.
 */
export const setLocationResults = (results: SearchResultItem[]) =>
  (observableState.locationResults = results)
/**
 * *(AI-generated)*
 * Returns the current location search results.
 */
export const locationResults = () => observableState.locationResults

// Set and retrieve landowner search results
/**
 * *(AI-generated)*
 * Sets the landowner search results.
 */
export const setLandownerResults = (results: SearchResultItem[]) =>
  (observableState.landownerResults = results)

/**
 * *(AI-generated)*
 * Returns the current landowner search results.
 */
export const landownerResults = () => observableState.landownerResults

// Set and retrieve total count of location search results
/**
 * *(AI-generated)*
 * Sets the total location results count.
 */
export const setTotalLocationResults = (count: number) =>
  (observableState.totalLocationResults = count)
/**
 * *(AI-generated)*
 * Returns the total location results count.
 */
export const totalLocationResults = () => observableState.totalLocationResults

// Set and retrieve total count of landowner search results
/**
 * *(AI-generated)*
 * Sets the total landowner results count.
 */
export const setTotalLandownerResults = (count: number) =>
  (observableState.totalLandownerResults = count)
/**
 * *(AI-generated)*
 * Returns the total landowner results count.
 */
export const totalLandownerResults = () => observableState.totalLandownerResults

// Set and retrieve the zero query search scope
// where a search scope is { name: 'scope name', slug: 'scope-slug', icon: 'icon-name' }
/**
 * *(AI-generated)*
 * Sets the zero query search scope.
 */
export const setZeroQuerySearchScope = (scopeCategory: string | null) =>
  (observableState.zeroQueryScope = scopeCategory)
/**
 * *(AI-generated)*
 * Returns the current zero query search scope.
 */
export const zeroQuerySearchScope = () => observableState.zeroQueryScope

// Set and retrieve the map view (View Map toggle button)
/**
 * *(AI-generated)*
 * Sets whether the explore map view is active.
 */
export const setIsExploreMapView = (viewMap: boolean) =>
  (observableState.isExploreMapView = viewMap)
/**
 * *(AI-generated)*
 * Returns whether the explore map view is active.
 */
export const getIsExploreMapView = () => observableState.isExploreMapView

// Set and retrieve the clickedBackFromResultCard state
/**
 * *(AI-generated)*
 * Sets whether the back-from-result-card state is active.
 */
export const setClickedBackFromResultCard = (clickedBack: boolean) =>
  (observableState.clickedBackFromResultCard = clickedBack)
/**
 * *(AI-generated)*
 * Returns the back-from-result-card state.
 */
export const clickedBackFromResultCard = () =>
  observableState.clickedBackFromResultCard

// Storage of search results by location
const searchResultsByLocation = new Map()
// Generate a storage key for searchResultsByLocation map from a search result
const getFeatureKey = ({ lat, lon, name }: SearchResultCacheKey) => {
  return `${Number(lat).toFixed(5)},${Number(lon).toFixed(5)},${name}`
}

// Set search type glyph icon map from remote source
/**
 * *(AI-generated)*
 * Fetches and sets the glyph icon map from the remote source.
 */
export const setGlyphIconMap = async () => {
  const response = await httpGetJson(`${ONXFONT_API_PATH}glyphmap.json`)
  if (!response) {
    return
  }

  // CHECK: set type for the glyphIconMap?
  observableState.glyphIconMap = response as Record<string, unknown>
}

/**
 * *(AI-generated)*
 * Returns the cached glyph icon map used for search result rendering.
 */
export const getGlyphIconMap = () => observableState.glyphIconMap

/**
 * Get a feature by name in location from search result cache
 * @param lat - Latitude of the feature
 * @param lon - Longitude of the feature
 * @param name - Name of the feature
 * @return - Search result json
 */
export const getFeatureAtLocation = ({
  lat,
  lon,
  name,
}: SearchResultCacheKey) =>
  searchResultsByLocation.get(getFeatureKey({ lat, lon, name }))

/**
 * Convert current camera location to bbox for search service request
 */
export const getBbox = async (
  enforceBbox: boolean = false
): Promise<BBox | null> => {
  await mapReadyPromise
  const currentCam = camera()

  // Use camera zoom
  const zoom = currentCam?.location.height || MAP_MIN_ZOOM

  // If we are zoomed way out don't include bounding box in search
  const hasFilter = hasMinMaxFilter(SEARCH_FILTER_KEYS.ACREAGE)
  if (zoom < 5 && !hasFilter) {
    return null
  }
  // Use coordinate of camera location
  // Get the camera location as a point or fallback to a default point of center of US
  const searchPoint: Feature<Point> =
    currentCam && currentCam?.location
      ? currentCam.location.toTurfPoint()
      : point([-98.5795, 39.8283])

  // Min map zoom level
  const zoomMin = 22 * 100

  // Max map zoom level
  const zoomMax = 1 * 100

  // Min search buffer in miles
  const minMiles = 20

  // Max search buffer in miles
  const maxMiles = 300

  // Convert zoom level to miles
  const searchBufferInMiles = remapRange(
    zoomMin,
    zoomMax,
    minMiles,
    maxMiles
  )(zoom * 100)

  // Create search box
  // If using enforcebbox param, only search visible region of map
  // Otherwise create a buffer
  const bufferedPoint = buffer(searchPoint, searchBufferInMiles, {
    units: 'miles',
  })

  let bboxToUse = null
  if (enforceBbox) {
    bboxToUse = bbox(visibleRegion())
  } else if (bufferedPoint) {
    bboxToUse = bbox(bufferedPoint)
  }

  return bboxToUse
}

/**
 * Get bounding box param for supergraph call
 * @param enforceBbox - Whether to restrict results to only those in the current viewport
 * @returns object with bounds
 */
const getBounds = async (enforceBbox = false) => {
  const bbox = await getBbox(enforceBbox)
  let bounds = null
  if (bbox) {
    const [left, bottom, right, top] = bbox
    bounds = { bottom, left, right, top }
  }
  return bounds
}

/**
 * Make a location search request via supergraph
 */
export const searchLocationByGraphQl = async (
  /**
   * *(AI-generated)*
   * User search query
   */
  query: string = '',
  /**
   * *(AI-generated)*
   * Number of search results that should be returned
   */
  size: number = 25,
  /**
   * *(AI-generated)*
   * Location to search
   */
  location?: Geographic,
  /**
   * *(AI-generated)*
   * Restrict results to only those in the current viewport
   */
  enforceBbox: boolean = false,
  /**
   * *(AI-generated)*
   * Any search filter params
   */
  filters: SearchFilter = {}
): Promise<SearchRequestReturnType> => {
  const locLatLon =
    location?.latitude && location?.longitude
      ? { lat: location.latitude, lon: location.longitude }
      : undefined

  const bounds = (await getBounds(enforceBbox)) || undefined

  // Add a state filter to the search filters
  const states = getStatesFilter()
  let statesFilter = null
  if (states.length) {
    statesFilter = getIsoStateNames(states)
  }

  // Add a private land entitlement filter for users who are not entitled to
  // receive private land data in their search results
  let privateLandFilter = null
  if (!getIsEntitledForPrivateLandData()) {
    privateLandFilter = gqlFilterExpressionToJson(
      gqlFilterSlugNotEquals('searchScopeFilter', 'private')
    )
  }

  let zeroQueryFilter = null
  const currentZeroQueryScope = zeroQuerySearchScope()
  if (isExploreEnabled.value && currentZeroQueryScope) {
    zeroQueryFilter = gqlFilterExpressionToJson(
      gqlFilterSlugEquals('searchScopeFilter', currentZeroQueryScope)
    )
  }

  const filter = {
    withinStates: statesFilter,
    ...(privateLandFilter && privateLandFilter),
    ...(zeroQueryFilter && zeroQueryFilter),
    ...(enforceBbox && { withinBounds: bounds }),
    ...filters,
  }

  return await searchLocation(query, size, locLatLon, bounds, filter).then(
    (res) => {
      // remove top level 'node' key, and initialize via SearchResultClass
      const results: SearchResultItem[] = res.edges.map((edge) =>
        getSearchResultDefault(cloneDeep(edge.node))
      )

      // Store results in map
      storeResultsInCacheMap(results)

      let estimatedTotal = results.length

      // Graphql does not return an exact total count
      // If there is another page estimate it's at least +1 results
      if (res.pageInfo?.hasNextPage) {
        estimatedTotal++
      }

      // slice the results by the requested size to make sure the list is the right length
      return { results: results.slice(0, size), total: estimatedTotal }
    }
  )
}

/**
 * Make a private land (landowner) search request via supergraph
 */
export const searchLandownerByGraphQl = async (
  /**
   * *(AI-generated)*
   * User search query
   */
  query: string = '',
  /**
   * *(AI-generated)*
   * Number of search results that should be returned
   */
  size: number = 25,
  /**
   * *(AI-generated)*
   * Location to search
   */
  location?: Geographic,
  /**
   * *(AI-generated)*
   * Restrict results to only those in the current viewport
   */
  enforceBbox: boolean = false,
  /**
   * *(AI-generated)*
   * Any search filter params
   */
  filters: SearchFilter = {}
): Promise<SearchRequestReturnType> => {
  const locLatLon =
    location?.latitude && location?.longitude
      ? { lat: location.latitude, lon: location.longitude }
      : undefined

  const bounds = (await getBounds(enforceBbox)) || undefined

  // Add a state filter to the search filters
  const states = getStatesFilter()
  let statesFilter = null
  if (states.length) {
    statesFilter = getIsoStateNames(states)
  }

  const filter = {
    withinStates: statesFilter,
    ...(enforceBbox && { withinBounds: bounds }),
    ...filters,
  }

  return await searchPrivateLand(query, size, locLatLon, bounds, filter).then(
    (res) => {
      // remove top level 'node' key
      const results: SearchResultItem[] = res.edges.map((edge) =>
        getSearchResultDefault(cloneDeep(edge.node))
      )

      // Store results in map
      storeResultsInCacheMap(results)

      let estimatedTotal = results.length

      // Graphql does not return an exact total count
      // If there is another page estimate it's at least +1 results
      if (res.pageInfo?.hasNextPage) {
        estimatedTotal++
      }

      return { results: results.slice(0, size), total: estimatedTotal }
    }
  )
}

/**
 * Creates list of ISO 3166 formatted strings, with the URN prefix being optional
 * Returns a list of ISO formatted strings for each state/country
 */
export const getIsoStateNames = (
  states: string[],
  urnPrefix = true
): string[] => {
  const isoPrefix = urnPrefix ? 'urn:onx:3166.iso.org:' : ''

  const isoStateNames: string[] = []

  const stateCodes: string[] = Object.values(STATE_CODES)
  const countryCodes: string[] = Object.values(COUNTRY_CODES)
  const usStateCodes: string[] = Object.keys(US_STATE_CODES)

  states.forEach((stateAbbr) => {
    if (stateCodes.includes(stateAbbr)) {
      isoStateNames.push(`${isoPrefix}US-${stateAbbr.toUpperCase()}`)
    } else if (countryCodes.includes(stateAbbr)) {
      isoStateNames.push(`${isoPrefix}${stateAbbr.toUpperCase()}`)
    } else if (usStateCodes.includes(stateAbbr)) {
      isoStateNames.push(`${isoPrefix}${stateAbbr}`)
    }
  })

  return isoStateNames
}

/**
 * Checks if user is a premium user and which states
 * the user has access to
 */
export const getStatesFilter = (): string[] => {
  let states: string[] = []

  const splitResponse = split.getSplitConfigByKey(
    'fish_midwest_supported__states'
  )
  let supportedStates =
    (splitResponse?.config?.supportedStates as string[]) ?? []
  // Leave a fallback in for now in case Split config fails
  if (!supportedStates.length) {
    supportedStates = SUPPORTED_STATES
  }
  states = supportedStates
  return states
}

/**
 * Map coordinate result data to Supergraph response API response
 */
export const convertCoordResultToApiResponseType = (
  apiResults: CoordinateResult[]
): SearchResultItem[] => {
  return apiResults.map((result) => {
    const mappedPhotos = [{ contentUrl: result.photo || '' }]

    const mappedResult: SearchResultItem = {
      countyName: result.county,
      nearby: result.nearby,
      item: {
        geometry: {
          coordinates: [
            Number(result.location?.lon),
            Number(result.location?.lat),
          ],
          type: 'Point',
        },
        id: '',
        name: result.name,
        onxType: result.type,
        photos: mappedPhotos,
        point: {
          coordinates: [
            Number(result.location?.lon),
            Number(result.location?.lat),
          ],
          type: 'Point',
        },
        searchScopes: [],
        identifiers: [],
        presentation: [],
        richPlaceID: null,
      },
    }
    return getSearchResultDefault(mappedResult)
  })
}

/**
 * Store search results in cache map
 */
const storeResultsInCacheMap = (results: SearchResultItem[]) => {
  results.forEach((result) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const item = result.item!

    const lon = item.point?.coordinates[0] ?? 0
    const lat = item.point?.coordinates[1] ?? 0
    const name = item.name as string
    searchResultsByLocation.set(getFeatureKey({ lat, lon, name }), result)
  })
}

/**
 * Make a location and landowner search request
 */
export const searchLandownerAndLocation = async (
  query: string,
  location: Geographic
) => {
  await split.splitClientsReady()
  return Promise.all([
    searchLocationByGraphQl(query, undefined, location),
    searchLandownerByGraphQl(query, undefined, location),
  ])
}

/**
 * Can the search result id be used for lookup in the rich content service
 */
export const isSearchIdUsableForRichContentQuery = (
  result: SearchResultItem
): boolean => {
  const {
    item: { richPlaceID, searchScopes },
  } = result

  if (richPlaceID) {
    return true
  }
  return ['adventures', 'routes', 'bkcy_trails'].includes(
    searchScopes?.[0]?.slug as string
  )
}

export const activeLastSearchTerm = computed(
  () =>
    observableState.lastQueryInputFetchedLocationSearch ||
    observableState.lastQueryInputFetchedLandownerSearch ||
    ''
)

export const searchExamples = computed(
  () => examplesByVertical['fish'][searchMode()]
)

// History
export const searchHistory = computed<SearchHistoryType>(() =>
  getItem(`fishSearchHistory`)
)

export const searchHistoryDisplay = computed(() => {
  const fullHistory = searchHistory.value
  return fullHistory?.slice(0, 5) ?? []
})

/**
 * *(AI-generated)*
 * Adds a query to the search history, maintaining a capped list.
 */
export const addQueryToHistory = (query: string) => {
  // If there is a new search input, always put it into the first space of the saveHistory list
  if (query) {
    const tempHistory = searchHistory.value.filter(
      (item) => item.toLowerCase() !== query.toLowerCase()
    )

    // limit history to max items
    const limitedHistory = tempHistory.slice(0, HISTORY_ITEM_LIMIT - 1)

    saveItem(`fishSearchHistory`, [query, ...limitedHistory])
  }
}

/**
 * *(AI-generated)*
 * Removes an item from the search history.
 */
export const removeFromHistory = (item: string) => {
  const newHistory = searchHistory.value.filter(
    (historyItem) => historyItem !== item
  )

  saveItem(`fishSearchHistory`, newHistory)

  marketingEventService.publish('delete_recent_search_result')
}

/**
 * Given the passed in result, redirect to the appropriate card
 */
export const redirectToSearchResult = (
  result: SearchResultProps,
  preserveOrientation: boolean = false,
  backLinkRoute: string = SEARCH_BACK_ROUTE
) => {
  const {
    item: { name, id, onxType, point },
    about,
  } = result
  const coordinates = point?.coordinates ?? [0, 0]

  const graphId = about?.id
  const graphType = about?.__typename

  const routerState = {
    origin: CARD_ORIGINS.SEARCH,
    backLinkLabel: 'Back',
    backLinkRoute,
    fromSearch: true,
  }

  if (graphId && graphType) {
    trackUserAction({
      actionType: 'FindAction',
      entityType: graphType as KgType,
      entityId: graphId,
      entityName: name,
    })

    const route = getRouterDestinationForKgType({
      id: graphId,
      typename: graphType as KgType,
      routeState: routerState,
      about,
    })
    if (route) {
      router.push(route).catch(() => {})
      return
    }
  }

  const lat = Number(coordinates[1]).toFixed(5)
  const lon = Number(coordinates[0]).toFixed(5)
  const app = 'fish'

  switch (onxType) {
    case 'coordinate': {
      router
        .push({
          name: `query-${app}-overview`,
          params: {
            coord: `${lat},${lon}`,
          },
          state: routerState,
          query: { coordSearch: 'true', search: 'true' },
        })
        .catch(() => {})
      break
    }
    default: {
      const rcQuery = isSearchIdUsableForRichContentQuery(result)
      const identifier = rcQuery ? { id } : { name }
      router
        .push({
          name: `query-${app}-overview`,
          params: {
            coord: `${lat},${lon}`,
          },
          state: {
            ...routerState,
            preserveOrientation,
          },
          query: { ...identifier, search: 'true' },
        })
        .catch(() => {})
    }
  }
}

/**
 * Is the current user entitled to any private land data (regardless of state entitlement)
 * @returns boolean
 */
export const getIsEntitledForPrivateLandData = () => {
  // available to Elite users only (and employees)
  return (
    subscriptionService.isElite() ||
    subscriptionService.isEliteTrial() ||
    subscriptionService.isEmployee()
  )
}

/**
 * *(AI-generated)*
 * Performs a zero-query search and updates the location results state.
 */
export const performZeroQuerySearch = async (limit = 50) => {
  const { results, total } = await searchLocationByGraphQl(
    '',
    limit,
    undefined,
    true
  )
  setLocationResults(results)
  setTotalLocationResults(total)
  setIsLoading(false)
}

/**
 * Handle navigating a user back to search, when clicking the back
 * button from a search result card
 */
export const navigateUserBackToSearch = () => {
  setClickedBackFromResultCard(true)
  setSearchIsActive(true)
}

/**
 * *(AI-generated)*
 * Send the marketing event for a selected search result
 */
export const sendSearchResultSelectedEvent = ({
  result,
  fromSearchMarker = false,
  fromAutocompleteResults = false,
  query,
  zoom,
}: {
  result: SearchResultProps
  fromSearchMarker?: boolean
  fromAutocompleteResults?: boolean
  query: string
  zoom?: number
}) => {
  const {
    item: { name, onxType, point, searchScopes },
    search_rank,
  } = result
  const coordinates = point?.coordinates ?? [0, 0]

  const [lon, lat] = coordinates
  const currentSearchMode = searchMode()
  const formattedZoom = zoom ? parseFloat(zoom.toFixed(1)) : null

  // Determine search result category
  let category = null
  if (searchScopes?.length) {
    category = searchScopes[0].name
  } else if (onxType?.toLowerCase() === 'coordinate') {
    category = 'coordinates'
  }

  // Determine search origin
  let origin: string = SEARCH_RESULT_SELECTED_ORIGINS.RESULT_LIST
  if (fromSearchMarker) {
    origin = SEARCH_RESULT_SELECTED_ORIGINS.RESULT_MAP_MARKER
  } else if (fromAutocompleteResults) {
    origin = SEARCH_RESULT_SELECTED_ORIGINS.AUTOCOMPLETE_RESULT_LIST
  }

  // Publish event
  marketingEventService.publish(EVENTS.SEARCH_RESULT_SELECTED, {
    category,
    type: onxType,
    selected_result: name,
    selected_result_lat: lat || null,
    selected_result_lon: lon || null,
    selected_result_list: currentSearchMode,
    query,
    origin,
    search_rank,
    search_type: currentSearchMode,
    zoom_level: formattedZoom,
  })
}

/**
 * Handle the selection of a search result.
 */
export const handleSearchResultSelection = ({
  result,
  query,
  fromSearchMarker = false,
  fromAutocompleteResults = false,
  mapMarkersEnabled = false,
}: {
  result: SearchResultProps
  query: string
  fromSearchMarker?: boolean
  fromAutocompleteResults?: boolean
  mapMarkersEnabled?: boolean
}) => {
  // Store result in cache map for access by map feature query
  storeResultsInCacheMap([result])

  // Add to search history
  addQueryToHistory(query)

  // Send event
  sendSearchResultSelectedEvent({
    result,
    fromSearchMarker,
    fromAutocompleteResults,
    query,
    zoom: camera()?.location?.height,
  })

  redirectToSearchResult(result, fromSearchMarker)

  // Set selected marker when using search map markers
  if (mapMarkersEnabled) {
    addSelectedResultMarkerToMap(result)
  }

  // Hide search results drawer
  setSearchIsActive(false)
}

/**
 * *(AI-generated)*
 * Handle the selection of an Autocomplete result
 */
export const handleAutocompleteSelection = async ({
  result,
  index,
  onResultSelected,
  onSuggestionSelected,
}: {
  result: AutocompleteResult
  index: number
  onResultSelected: (result: SearchResultProps) => void
  onSuggestionSelected: (query: string) => void
}) => {
  const resultAbout = result.about

  if (resultAbout?.type) {
    // For a suggested Knowledge Graph result
    if (
      isAutocompleteKgResult(result) &&
      isSupportedKgType(resultAbout.type as KgType)
    ) {
      // Autocomplete Knowledge Graph results do not have location data, so the coordinates are set to [0,0]
      // knowing that the selected_search_result marketing event will reflect the lat/lon as null
      const PLACEHOLDER_COORDINATES: Position = [0, 0]
      const type = resultAbout.type as SearchResultAboutTypename

      const kgResult: SearchResultProps = {
        item: {
          name: resultAbout.name,
          id: resultAbout.id,
          onxType: resultAbout.additionalType,
          point: {
            type: 'Point' as const,
            coordinates: PLACEHOLDER_COORDINATES,
          },
          searchScopes: [],
        },
        about: {
          __typename: type,
          id: resultAbout.id,
          name: resultAbout.name,
        } as SearchResultAbout,
        search_rank: index + 1,
      }

      onResultSelected(kgResult)
    }
    // For a suggested SearchResult, or a KG type with no fish route, fetch the
    // result via the id and navigate to it
    else {
      const response = await fetchAutocompleteResultItemData({
        query: result.query,
        type: resultAbout.type,
        id: resultAbout.id,
      })
      // Normalize to the same shape as a full search result so the query card
      // renders identical feature info regardless of how it was reached.
      // Clone first - getSearchResultDefault mutates its argument and Apollo
      // hands back frozen objects
      const resultProps: SearchResultProps = {
        ...getSearchResultDefault(cloneDeep(response)),
        search_rank: index + 1,
      }
      onResultSelected(resultProps)
    }
  } else {
    // If there is no resultAbout data, it is a search term suggestion
    onSuggestionSelected(result.query)
  }
}

/**
 * Add the marker for a single selected search result on the map
 */
export const addSelectedResultMarkerToMap = (result: SearchResultProps) => {
  if (isSearchPinsV2Enabled()) {
    if (!searchPinFeatures().length) {
      // If there are not already search pin features on the map, generate and add the selected pin
      const pinFeature = generatePinFeatureFromSearchResult({
        id: result.item.id,
        name: result.item.name,
        type: result.item.onxType || '',
        location: result.item.point?.coordinates ?? [0, 0],
        index: result.search_rank,
      })
      displaySearchResultPins([pinFeature])
    }

    setSearchPinSelected(result.item.id)
    return
  }

  const selectedMarker = generateGlyphMarkers(
    [result],
    GLYPH_FONTS.SELECTED_CIRCLE,
    true
  )
  replaceMarkers(selectedMarker)
}

/**
 * *(AI-generated)*
 * Determines if the Search Pins V2 feature flag is enabled
 */
export const isSearchPinsV2Enabled = () => {
  return !!isActive('fox_search_pins_v2')
}
